import { AffiliateCommissionStatus, AffiliateEarningsResponse } from '@avanzu/types';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { apiClient } from '../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../utils/formatDate';

const formatCents = (cents: number, currency: string) =>
  `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;

const statusColor = (
  status: AffiliateCommissionStatus
): 'warning' | 'success' | 'error' => {
  switch (status) {
    case AffiliateCommissionStatus.PAID:
      return 'success';
    case AffiliateCommissionStatus.REVERSED:
      return 'error';
    default:
      return 'warning';
  }
};

const EarningsPage: React.FC = () => {
  const [earnings, setEarnings] = useState<AffiliateEarningsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const response = await apiClient.get('/affiliate/earnings');
        if (response.ok) setEarnings(await response.json());
      } catch (err) {
        console.error('Failed to load earnings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currency = earnings?.totals.currency ?? 'aud';

  return (
    <Stack spacing={3}>
      <PaperSection title="Earnings">
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {formatCents(earnings?.totals.pendingAmount ?? 0, currency)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Paid
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {formatCents(earnings?.totals.paidAmount ?? 0, currency)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Reversed
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {formatCents(earnings?.totals.reversedAmount ?? 0, currency)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary">
          You earn {((earnings?.commissionRateBps ?? 0) / 100).toFixed(0)}% of each referred
          organisation's first paid project. Pending commissions are paid out by the Avanzu team;
          refunded payments are reversed.
        </Typography>
      </PaperSection>

      <PaperSection title="Commission history">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Charged</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Earned</TableCell>
                <TableCell>Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!earnings || earnings.earnings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No commissions yet — you'll earn one when a referred organisation pays for
                      its first project
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                earnings.earnings.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{e.organisationName}</TableCell>
                    <TableCell>{e.projectName || '-'}</TableCell>
                    <TableCell>{formatCents(e.grossAmount, e.currency)}</TableCell>
                    <TableCell>{formatCents(e.commissionAmount, e.currency)}</TableCell>
                    <TableCell>
                      <Chip label={e.status} size="small" color={statusColor(e.status)} />
                    </TableCell>
                    <TableCell>{formatDate(e.createdAt, DateFormatOptions.DATEONLY)}</TableCell>
                    <TableCell>
                      {e.paidAt ? formatDate(e.paidAt, DateFormatOptions.DATEONLY) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PaperSection>
    </Stack>
  );
};

export default EarningsPage;
